/**
 * Asset utilities for consistent path resolution
 *
 * This module provides utilities for resolving asset paths consistently
 * across different deployment environments (production, PR previews, etc.)
 */

/**
 * Get the current environment variables for asset resolution
 * This function can be mocked in tests
 */
export function getAssetEnv() {
  return {
    publicUrl: import.meta.env.VITE_PUBLIC_URL || '',
    baseUrl: import.meta.env.BASE_URL || '/',
  };
}

/**
 * Resolves an asset path using provided environment variables
 * This function is the core logic and is easily testable
 *
 * @param assetPath - The relative asset path (e.g., '/assets/logo.png' or 'assets/logo.png')
 * @param publicUrl - The public URL from VITE_PUBLIC_URL
 * @param baseUrl - The base URL from BASE_URL
 * @returns The complete asset URL
 */
export function resolveAssetPathWithEnv(
  assetPath: string,
  publicUrl: string,
  baseUrl: string
): string {
  // Remove leading slash if present to ensure consistent handling
  const normalizedPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;

  // Use publicUrl if available, otherwise fall back to baseUrl
  const resolvedBase = publicUrl || baseUrl;

  // Ensure proper path joining
  if (resolvedBase === '' || resolvedBase === '/') {
    return `/${normalizedPath}`;
  }

  // Remove trailing slash from resolvedBase and ensure leading slash on result
  const cleanBase = resolvedBase.replace(/\/$/, '');
  return `${cleanBase}/${normalizedPath}`;
}

/**
 * Resolves an asset path using the configured public URL or base URL
 *
 * @param assetPath - The relative asset path (e.g., '/assets/logo.png' or 'assets/logo.png')
 * @returns The complete asset URL
 *
 * @example
 * ```ts
 * const logoUrl = resolveAssetPath('/assets/logo.png');
 * // In production: '/assets/logo.png'
 * // In PR preview: '/preview/pr-123/assets/logo.png'
 * ```
 */
export function resolveAssetPath(assetPath: string): string {
  const { publicUrl, baseUrl } = getAssetEnv();
  return resolveAssetPathWithEnv(assetPath, publicUrl, baseUrl);
}

/**
 * Resolves a public directory asset path
 * This is specifically for assets in the public/ directory
 *
 * @param filename - The filename in the public directory (e.g., 'favicon.png', 'manifest.json')
 * @returns The complete asset URL
 */
export function resolvePublicAsset(filename: string): string {
  return resolveAssetPath(filename);
}

/**
 * Resolves an asset from the public/assets directory
 *
 * @param filename - The filename in public/assets (e.g., 'logo.png', 'icon.svg')
 * @returns The complete asset URL
 */
export function resolveAssetsPath(filename: string): string {
  return resolveAssetPath(`assets/${filename}`);
}
